import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { CommentOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { aboutOf, type DiscussionCtx, sendReply, type ThreadRef } from "../../lib/conversation.ts";
import { isReport, REPORT_EDGE } from "../../lib/reports.ts";
import { canComment, me } from "../../lib/state.ts";
import { Avatar } from "../../ui/Avatar.tsx";
import { Link } from "../../ui/Button.tsx";
import { LINK } from "../../ui/styles.ts";
import { CommentView } from "./comment.tsx";
import { Composer } from "./composer.tsx";

/**
 * A discussion as a social media post: what it's about on top, as a preview (`attachment`), then its first comment
 * with the author's avatar, its replies below (the earlier ones a link away), and a box to reply, always there.
 */
export function Post({
  ctx,
  at,
  root,
  replies,
  attachment,
  more,
}: {
  ctx: DiscussionCtx;
  at: ThreadRef;
  root: CommentOut;
  replies: readonly CommentOut[];
  attachment?: ComponentChildren;
  more?: { count: number; href: string } | undefined;
}) {
  const [asked, setAsked] = useState(0);
  const total = replies.length + (more?.count ?? 0);
  const reply = () => setAsked(asked + 1);
  const writing = canComment(ctx.space);
  return (
    <li
      class={`flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white p-4 ${isReport(root) ? REPORT_EDGE : ""}`}
    >
      {attachment ?? null}
      <CommentView ctx={ctx} c={root} post={{ avatar: 40, replies: total }} onReply={reply} />
      {total > 0 || writing ? (
        <div class="ml-[52px] flex flex-col gap-3 border-t border-zinc-100 pt-3">
          {more && more.count > 0 ? (
            <Link href={more.href} class={`${LINK} text-xs`}>
              {t("timeline.more", { count: more.count })}
            </Link>
          ) : null}
          {replies.map((r) => (
            <CommentView key={r.id} ctx={ctx} c={r} post={{ avatar: 28 }} onReply={reply} />
          ))}
          {writing ? (
            <div class="flex gap-2">
              <Avatar id={me.value?.id} name={me.value?.name ?? ""} size={28} />
              <div class="min-w-0 flex-1">
                <Composer
                  key={asked}
                  space={ctx.space}
                  about={aboutOf(ctx)}
                  compact={true}
                  autoFocus={asked > 0}
                  placeholder={t("comments.replyPlaceholder")}
                  sendLabel={t("comments.reply")}
                  send={(body) => sendReply(ctx, at, root, body)}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
